import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';

/**
 * Handles the impersonation token exchange.
 * Admin console opens this page with ?code=<opaque_code>.
 * The page exchanges the code for tokens, logs in, and redirects to /home.
 */
export const ImpersonatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithTokens } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchangedRef = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setError('No impersonation code provided.');
      return;
    }

    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const exchange = async () => {
      try {
        const response = await authApi.exchangeImpersonationCode(code);
        loginWithTokens(response.accessToken, response.refreshToken, response.user);
        navigate('/home', { replace: true });
      } catch (err: any) {
        exchangedRef.current = false;
        setError(err.message || 'Invalid or expired impersonation link.');
      }
    };

    exchange();
  }, [searchParams, loginWithTokens, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-medical-50">
        <div className="bg-white rounded-xl shadow-sm border border-medical-100 p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-medical-900 mb-2">Impersonation Failed</h1>
          <p className="text-medical-600 mb-4">{error}</p>
          <a href="/signin" className="text-primary-600 hover:text-primary-700 font-medium">
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-medical-50">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-medical-600">Signing in...</p>
      </div>
    </div>
  );
};
